import React from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../common/hooks/hooks";
import { setCurrentStep } from "../../checkoutSlice";
import { selectSelectedPaymentMethod } from "../../checkoutSelectors";
import { useGetCheckoutDataQuery } from "../../api/checkoutApi";
import { useGetCartQuery } from "../../../cart/api/cartApi";
import CheckoutStepLayout from "../common/CheckoutStepLayout";
import OrderReview from "./OrderReview/OrderReview";
import PaymentOptions from "./PaymentOptions";
import PlaceOrderActions from "./PlaceOrderActions";
import CustomerNoteForm from "./CustomerNoteForm";
import { useCheckoutSubmit } from "../../hooks/useCheckoutSubmit";
import DeliveryEstimateDisplay from "../../../product/components/common/DeliveryEstimateDisplay";

const PaymentStep: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // Selectors
  const selectedPaymentMethod = useAppSelector(selectSelectedPaymentMethod);

  // Fetch data
  const { data: checkoutData, isLoading: isCheckoutLoading } =
    useGetCheckoutDataQuery();
  const { data: cartData, isLoading: isCartLoading } = useGetCartQuery();

  // Custom Hook for Logic
  const { submitOrder, isProcessing } = useCheckoutSubmit();

  const handleNavigation = (step: "billing" | "shipping") => {
    dispatch(setCurrentStep(step));
  };

  const handlePlaceOrder = () => {
    if (!selectedPaymentMethod || !checkoutData) {
      toast.error(t("paymentOptions.noPaymentMethods"));
      return;
    }
    submitOrder(checkoutData, selectedPaymentMethod);
  };

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  if (isCheckoutLoading || isCartLoading) {
    return (
      <div className="checkout-step">
        <div className="loader-spinner"></div>
      </div>
    );
  }

  return (
    <>
      {/* --- ORDER REVIEW --- */}
      <OrderReview
        checkoutData={checkoutData}
        cartData={cartData}
        onEditBilling={() => handleNavigation("billing")}
        onEditShipping={() => handleNavigation("shipping")}
      />

      {/* --- DELIVERY ESTIMATION --- */}
      <DeliveryEstimateDisplay
        estimate={checkoutData?.deliveryEstimation}
        className="checkout-payment-delivery"
      />

      {/* --- CUSTOMER NOTE --- */}
      <CustomerNoteForm />

      {/* --- PAYMENT FORM --- */}
      <CheckoutStepLayout
        title={t("paymentStep.stepTitle", "Payment")}
        onSubmit={onSubmit}
        isProcessing={false}
        nextButtonText=""
        showNextButton={false}
        showPrevButton={true}
        prevButtonType="button"
        onPrevious={() => handleNavigation("shipping")}
      >
        <PaymentOptions />
      </CheckoutStepLayout>

      {/* --- PORTAL ACTIONS (BUTTON + CHECKBOX) --- */}
      <PlaceOrderActions
        onPlaceOrder={handlePlaceOrder}
        disabled={!selectedPaymentMethod}
        isLoading={isProcessing}
        selectedPaymentMethod={selectedPaymentMethod}
      />
    </>
  );
};

export default PaymentStep;
